import React, { useState, useEffect } from 'react';
import { productsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Pencil, Trash2, Package, AlertTriangle, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';

const emptyProduct = {
  code: '',
  description: '',
  price: '',
  price_2: '',
  price_3: '',
  cost: '',
  iva: '22',
  stock: '',
  unit: 'pz',
  pieces_per_box: '1'
};

const IVA_OPTIONS = ['4', '5', '10', '22'];

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async (searchTerm = '') => {
    try {
      const response = await productsApi.getAll(searchTerm);
      setProducts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchProducts(value);
  };

  const openCreateDialog = () => {
    setSelectedProduct(null);
    setFormData(emptyProduct);
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      code: product.code,
      description: product.description,
      price: product.price?.toString() || '',
      price_2: product.price_2?.toString() || '',
      price_3: product.price_3?.toString() || '',
      cost: product.cost?.toString() || '',
      iva: product.iva?.toString() || '22',
      stock: product.stock?.toString() || '',
      unit: product.unit,
      pieces_per_box: product.pieces_per_box?.toString() || '1'
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description) {
      toast.error('La descrizione è obbligatoria');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        price_2: parseFloat(formData.price_2) || 0,
        price_3: parseFloat(formData.price_3) || 0,
        cost: parseFloat(formData.cost) || 0,
        iva: parseFloat(formData.iva) || 22,
        stock: parseFloat(formData.stock) || 0,
        pieces_per_box: parseFloat(formData.pieces_per_box) || 1,
        unit: formData.unit || 'pz',
      };
      
      if (selectedProduct) {
        await productsApi.update(selectedProduct.id, data);
        toast.success('Prodotto aggiornato');
      } else {
        await productsApi.create(data);
        toast.success('Prodotto creato');
      }
      setDialogOpen(false);
      fetchProducts(search);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await productsApi.delete(selectedProduct.id);
      toast.success('Prodotto eliminato');
      setDeleteDialogOpen(false);
      fetchProducts(search);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const handleExport = async () => {
    try {
      const response = await productsApi.exportCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prodotti_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV scaricato');
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const csv_text = evt.target.result;
        const result = await productsApi.importCsv(csv_text, false);
        toast.success(`${result.data.created} creati, ${result.data.updated} aggiornati`);
        if (result.data.errors?.length) {
          toast.warning(`${result.data.errors.length} errori (vedi console)`);
          console.warn('Import errors:', result.data.errors);
        }
        fetchProducts(search);
      } catch (error) {
        toast.error('Errore nell\'import');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const calculateMargin = (price, cost) => {
    if (!price || !cost || cost === 0) return 0;
    return ((price - cost) / cost * 100).toFixed(1);
  };

  return (
    <div data-testid="products-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Prodotti</h1>
          <p className="text-muted-foreground text-sm">Gestisci il catalogo prodotti</p>
        </div>
        <Button data-testid="new-product-btn" onClick={openCreateDialog} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          NUOVO PRODOTTO
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button data-testid="export-products-btn" onClick={handleExport} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" /> Esporta CSV
        </Button>
        <label>
          <input data-testid="import-products-input" type="file" accept=".csv" onChange={handleImport} className="hidden" />
          <Button asChild variant="outline" size="sm" type="button" className="cursor-pointer">
            <span><Upload className="w-4 h-4 mr-2" /> Importa CSV</span>
          </Button>
        </label>
        <p className="text-xs text-muted-foreground self-center ml-2">
          Formato: code;description;price;price_2;price_3;cost;iva;stock;unit;pieces_per_box (separatore ;)
        </p>
      </div>

      {/* Search */}
      <Card className="card-swiss">
        <CardContent className="pt-4 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="search-products-input"
              placeholder="Cerca per codice o descrizione..."
              value={search}
              onChange={handleSearch}
              className="pl-10 h-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Caricamento...</div>
            </div>
          ) : products.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2">Codice</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2">Descrizione</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2 text-center">Pz/Box</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2 text-center">Mag. (box)</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2 text-right">Costo</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2 text-right">Vendita</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2 text-center">IVA</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2 text-right">Marg.</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground h-8 px-2 w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id} className="hover:bg-muted/30">
                    <TableCell className="py-2 px-2 text-xs font-['JetBrains_Mono'] font-medium">
                      {product.code}
                    </TableCell>
                    <TableCell className="py-2 px-2 text-xs">{product.description}</TableCell>
                    <TableCell className="py-2 px-2 text-xs text-center font-['JetBrains_Mono']">
                      {product.pieces_per_box || 1}
                      <span className="ml-0.5 text-[10px] text-muted-foreground uppercase">{product.unit === 'kg' ? 'kg' : 'pz'}</span>
                    </TableCell>
                    <TableCell className="py-2 px-2 text-xs text-center">
                      <span className={`font-['JetBrains_Mono'] ${product.stock <= 5 ? 'text-destructive font-semibold' : ''}`}>
                        {product.stock || 0}
                        {product.stock <= 5 && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                      </span>
                    </TableCell>
                    <TableCell className="py-2 px-2 text-xs text-right font-['JetBrains_Mono'] text-muted-foreground">
                      {formatCurrency(product.cost)}
                    </TableCell>
                    <TableCell className="py-2 px-2 text-xs text-right font-['JetBrains_Mono']">
                      {formatCurrency(product.price)}
                    </TableCell>
                    <TableCell className="py-2 px-2 text-xs text-center font-['JetBrains_Mono']">
                      {product.iva || 22}%
                    </TableCell>
                    <TableCell className="py-2 px-2 text-xs text-right">
                      <span className={`font-['JetBrains_Mono'] ${
                        calculateMargin(product.price, product.cost) > 20 ? 'text-green-600' : 
                        calculateMargin(product.price, product.cost) > 0 ? 'text-yellow-600' : 'text-destructive'
                      }`}>
                        {calculateMargin(product.price, product.cost)}%
                      </span>
                    </TableCell>
                    <TableCell className="py-2 px-2">
                      <div className="flex gap-0.5">
                        <Button
                          data-testid={`edit-product-${product.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(product)}
                          className="h-7 w-7"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          data-testid={`delete-product-${product.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(product)}
                          className="h-7 w-7 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun prodotto trovato</p>
              <Button onClick={openCreateDialog} className="mt-4 btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                AGGIUNGI IL PRIMO PRODOTTO
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] font-semibold">
              {selectedProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-uppercase">Codice <span className="text-muted-foreground normal-case">(auto se vuoto)</span></Label>
                  <Input
                    data-testid="product-code-input"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Lascia vuoto: auto 001, 002..."
                    className="font-['JetBrains_Mono'] h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Unità per Box</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="product-pieces-per-box-input"
                      type="number"
                      step="any"
                      min="0"
                      value={formData.pieces_per_box}
                      onChange={(e) => setFormData({ ...formData, pieces_per_box: e.target.value })}
                      placeholder="1"
                      className="font-['JetBrains_Mono'] h-9 flex-1"
                    />
                    <div className="flex border-2 border-blue-200 rounded-md p-0.5 bg-blue-50/40">
                      <button
                        type="button"
                        data-testid="product-unit-pz-btn"
                        onClick={() => setFormData({ ...formData, unit: 'pz' })}
                        className={`px-3 h-8 text-xs font-bold rounded-sm transition uppercase ${formData.unit === 'pz' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700/60 hover:bg-white/60'}`}
                      >
                        Pezzi
                      </button>
                      <button
                        type="button"
                        data-testid="product-unit-kg-btn"
                        onClick={() => setFormData({ ...formData, unit: 'kg' })}
                        className={`px-3 h-8 text-xs font-bold rounded-sm transition uppercase ${formData.unit === 'kg' ? 'bg-emerald-600 text-white shadow-md' : 'text-emerald-700/60 hover:bg-white/60'}`}
                      >
                        Kg
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Descrizione *</Label>
                <Input
                  data-testid="product-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione del prodotto"
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="label-uppercase">Costo (€)</Label>
                  <Input
                    data-testid="product-cost-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    placeholder="0.00"
                    className="font-['JetBrains_Mono'] h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">IVA %</Label>
                  <Select 
                    value={formData.iva} 
                    onValueChange={(value) => setFormData({ ...formData, iva: value })}
                  >
                    <SelectTrigger data-testid="product-iva-select" className="h-9 font-['JetBrains_Mono']">
                      <SelectValue placeholder="IVA" />
                    </SelectTrigger>
                    <SelectContent>
                      {IVA_OPTIONS.map((iva) => (
                        <SelectItem key={iva} value={iva}>{iva}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Magazzino (box)</Label>
                  <Input
                    data-testid="product-stock-input"
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="0"
                    className="font-['JetBrains_Mono'] h-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Listini di vendita (€)</Label>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    data-testid="product-price-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="Listino 1"
                    className="font-['JetBrains_Mono'] h-9"
                  />
                  <Input
                    data-testid="product-price2-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_2}
                    onChange={(e) => setFormData({ ...formData, price_2: e.target.value })}
                    placeholder="Listino 2 (Ingrosso)"
                    className="font-['JetBrains_Mono'] h-9"
                  />
                  <Input
                    data-testid="product-price3-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_3}
                    onChange={(e) => setFormData({ ...formData, price_3: e.target.value })}
                    placeholder="Listino 3 (Speciale)"
                    className="font-['JetBrains_Mono'] h-9"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">Lascia 0 per usare il Listino 1 come fallback</p>
              </div>
              {formData.cost && formData.price && (
                <div className="bg-muted/30 p-3 rounded-sm border border-border/50">
                  <p className="text-sm text-muted-foreground">
                    Margine: <span className="font-['JetBrains_Mono'] font-semibold text-foreground">
                      {formatCurrency(parseFloat(formData.price) - parseFloat(formData.cost))}
                    </span> ({calculateMargin(parseFloat(formData.price), parseFloat(formData.cost))}%)
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button data-testid="save-product-btn" type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Salvataggio...' : 'SALVA'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il prodotto "{selectedProduct?.description}"? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-product-btn"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
